// src/pages/ClientIntake.jsx
// Phase E1 — Public client intake portal. Accessed via /intake/:token,
// no KaiLux account required. Same pattern as the director sign-off portal.
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { getIntakePortal, submitIntakeText, submitIntakeFile, submitIntakeComplete } from '../api/kailux'
import Spinner from '../components/Spinner'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
const wrap = {
  minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
  color:T.tx, display:'flex', alignItems:'flex-start', justifyContent:'center',
  padding:'40px 16px',
}
const card = {
  width:'100%', maxWidth:'620px', background:T.s1, border:`1px solid ${T.bd}`,
  borderRadius:'16px', padding:'32px', boxShadow:T.shl,
}

export default function ClientIntake() {
  const { token } = useParams()
  const [data, setData]     = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState('')
  const [busy, setBusy]     = useState({})       // {itemId: true/false}
  const [textDrafts, setTextDrafts] = useState({}) // {itemId: text}
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const load = () => {
    getIntakePortal(token)
      .then(res => setData(res.data))
      .catch(err => setError(
        err.response?.status === 404
          ? "This link is invalid or has expired."
          : "We couldn't load this checklist. Please try again later."
      ))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [token])

  const setItemBusy = (id, val) => setBusy(b => ({ ...b, [id]: val }))

  const handleTextSave = async (itemId) => {
    const text = (textDrafts[itemId] || '').trim()
    if (!text) return
    setItemBusy(itemId, true)
    try {
      await submitIntakeText(token, { item_id: itemId, text })
      load()
    } catch {
      alert("Couldn't save your answer — please try again.")
    } finally {
      setItemBusy(itemId, false)
    }
  }

  const handleFileUpload = async (itemId, file) => {
    if (!file) return
    setItemBusy(itemId, true)
    try {
      await submitIntakeFile(token, itemId, file)
      load()
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed — please try again.")
    } finally {
      setItemBusy(itemId, false)
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError('')
    try {
      await submitIntakeComplete(token)
      load()
    } catch (err) {
      setSubmitError(err.response?.data?.detail || "Couldn't submit — please check all required items.")
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div style={wrap}><div style={card}>
      <div style={{ textAlign:'center', padding:'40px 0' }}><Spinner/></div>
    </div></div>
  }

  if (error) {
    return <div style={wrap}><div style={card}>
      <div style={{ textAlign:'center', padding:'24px 0' }}>
        <div style={{ marginBottom:'12px' }}><Lock size={34} strokeWidth={1.5} color={T.mt}/></div>
        <div style={{ fontSize:'16px', fontWeight:'700', marginBottom:'8px' }}>Link not available</div>
        <div style={{ fontSize:'14px', color:T.mt }}>{error}</div>
      </div>
    </div></div>
  }

  if (data.status === 'completed') {
    return <div style={wrap}><div style={card}>
      <div style={{ textAlign:'center', padding:'24px 0' }}>
        <div style={{ fontSize:'48px', marginBottom:'12px' }}><Icon name="check" strokeWidth={1.5} style={{ color: T.gn }} /></div>
        <div style={{ fontSize:'18px', fontWeight:'800', marginBottom:'8px',
          fontFamily:"'Syne',sans-serif" }}>All done — thank you!</div>
        <div style={{ fontSize:'14px', color:T.mt }}>
          {data.firm_name || 'Your accountant'} has been notified that you've completed everything.
          You can close this page now.
        </div>
      </div>
    </div></div>
  }

  const checklist = data.checklist || []
  const responses = data.responses || {}
  const completedCount = checklist.filter(c => responses[c.id]).length
  const requiredMissing = checklist.filter(c => c.required && !responses[c.id])

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
          <div style={{ width:'32px', height:'32px', background:T.prg,
            borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center',
            fontFamily:"'Syne',sans-serif", fontWeight:'800', color:T.onpri, fontSize:'14px' }}>K</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
        </div>

        <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800',
          margin:'18px 0 6px' }}>
          Information needed
        </h1>
        <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginBottom:'4px' }}>
          {data.firm_name && <>From <strong style={{ color:T.tx }}>{data.firm_name}</strong></>}
          {data.client_name && <> for <strong style={{ color:T.tx }}>{data.client_name}</strong></>}
        </div>
        <div style={{ fontSize:'13px', color:T.lt, marginBottom:'24px' }}>
          {completedCount} of {checklist.length} items completed
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
          {checklist.map((item) => {
            const done = !!responses[item.id]
            const isBusy = !!busy[item.id]
            return (
              <div key={item.id} style={{
                border:`1.5px solid ${done ? alpha(T.gn, 0.3) : T.bd}`,
                background: done ? T.gnd : T.s2,
                borderRadius:'12px', padding:'16px',
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'8px' }}>
                  <div style={{ fontSize:'14px', fontWeight:'700', lineHeight:'1.4' }}>
                    {item.label}
                    {item.required && !done && <span style={{ color:T.or, marginLeft:'4px' }}>*</span>}
                  </div>
                  {done && <span style={{ fontSize:'16px', color:T.gn, flexShrink:0 }}><Icon name="check" /></span>}
                </div>

                {done ? (
                  <div style={{ fontSize:'13px', color:T.mt, marginTop:'8px' }}>
                    {responses[item.id].text
                      ? <>You answered: <span style={{ color:T.lt }}>“{responses[item.id].text}”</span></>
                      : <>Uploaded: <span style={{ color:T.lt }}>{responses[item.id].file_name}</span></>}
                  </div>
                ) : item.type === 'text' ? (
                  <div style={{ marginTop:'10px', display:'flex', gap:'8px' }}>
                    <input
                      value={textDrafts[item.id] || ''}
                      onChange={e => setTextDrafts(d => ({ ...d, [item.id]: e.target.value }))}
                      placeholder="Type your answer…"
                      style={{ flex:1, padding:'9px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                        background:T.bg, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }}
                    />
                    <button
                      disabled={isBusy || !(textDrafts[item.id] || '').trim()}
                      onClick={() => handleTextSave(item.id)}
                      style={{ padding:'9px 18px', borderRadius:'8px', border:'none',
                        background: T.prg, opacity: isBusy ? 0.6 : 1,
                        color:T.onpri, fontFamily:"'Outfit',sans-serif", fontSize:'13px',
                        fontWeight:'700', cursor: isBusy ? 'default' : 'pointer' }}>
                      {isBusy ? '...' : 'Save'}
                    </button>
                  </div>
                ) : (
                  <div style={{ marginTop:'10px' }}>
                    <label style={{ display:'inline-block', padding:'9px 18px', borderRadius:'8px',
                      border:`1px dashed ${alpha(T.or, 0.38)}`, background:T.ord, color:T.or,
                      fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
                      cursor: isBusy ? 'default' : 'pointer' }}>
                      {isBusy ? 'Uploading…' : <><Icon name="attach" /> Choose file to upload</>}
                      <input type="file" disabled={isBusy} style={{ display:'none' }}
                        onChange={e => handleFileUpload(item.id, e.target.files?.[0])} />
                    </label>
                    <div style={{ fontSize:'12px', color:T.mt, marginTop:'6px' }}>
                      PDF, image, or document. Max 15MB.
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {submitError && (
          <div style={{ marginTop:'16px', padding:'10px 14px', borderRadius:'8px',
            background:alpha(T.rd, 0.1), border:`1px solid ${alpha(T.rd, 0.25)}`,
            color:T.rd, fontSize:'13px' }}>
            {submitError}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting || requiredMissing.length > 0}
          style={{ marginTop:'20px', width:'100%', padding:'12px',
            borderRadius:'8px', border:'none',
            background: requiredMissing.length > 0 ? T.bd : T.prg,
            color: requiredMissing.length > 0 ? T.mt : T.onpri,
            fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700',
            cursor: requiredMissing.length > 0 ? 'default' : 'pointer' }}>
          {submitting ? 'Submitting…'
            : requiredMissing.length > 0
              ? `Complete ${requiredMissing.length} more item${requiredMissing.length===1?'':'s'} to submit`
              : 'Submit checklist →'}
        </button>
      </div>
    </div>
  )
}
